import { useEffect, useState } from 'react';
import { type Connection, Connections } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Button, Facts, Section, SideSheet } from '@beyond-js/ui/react';
import { Permissions } from '../connect/permission';

/**
 * One connection's properties, opened from its row in the shared `SideSheet` (D160: its facts are its
 * properties): what the app can do there, each permission with what it does, then the product, the
 * organizations it reaches, where it answers and when it was connected, last used and ends, whole at
 * every width, with **Remove access** in the sheet's footer.
 *
 * **Change permissions** turns the permissions into checkboxes: unchecking one marks it to be removed,
 * and **Remove permissions** sends only that, after the page's confirmation (`onNarrow`). Nothing here
 * adds a permission: the app asks again and the person allows it at the consent.
 */
export function Details({ connection, reached, busy = false, onClose, onDisconnect, onNarrow }: {
	connection?: Connection;
	reached: (connection: Connection) => string;
	/** A narrowing the person confirmed is being sent */
	busy?: boolean;
	onClose: () => void;
	onDisconnect: (connection: Connection) => void;
	/** Asks to keep only `scopes`: the page confirms it first, then sends it */
	onNarrow: (connection: Connection, scopes: string[]) => void;
}) {
	const { text, date, language } = usePresence();
	const host = connection ? Connections.host(connection) : undefined;
	const [editing, setEditing] = useState(false);
	const [removed, setRemoved] = useState<string[]>([]);

	// Another connection, or the same one read again, starts without editing
	useEffect(() => {
		setEditing(false);
		setRemoved([]);
	}, [connection?.id, connection?.version]);

	const names = connection?.scopes.map(scope => scope.name) ?? [];
	const kept = names.filter(name => !removed.includes(name));


	const footer = !connection ? null : editing ? (
		<>
			<Button variant="primary" busy={busy} waiting={!removed.length || !kept.length} onClick={() => connection && onNarrow(connection, kept)}>
				{text('connections.permissions.remove')}
			</Button>
			<Button variant="quiet" disabled={busy} onClick={() => (setEditing(false), setRemoved([]))}>
				{text('action.cancel')}
			</Button>
		</>
	) : (
		<Button variant="danger" onClick={() => onDisconnect(connection)}>
			{text('connections.disconnect')}
		</Button>
	);

	return (
		<SideSheet open={Boolean(connection)} title={connection?.client.name ?? ''} labels={SideSheet.labels[language]} onClose={onClose} actions={footer}>
			{connection ? (
				<>
					<Section
						level={3}
						title={text('connections.scopes')}
						description={editing ? text('connections.permissions.hint', { client: connection.client.name }) : null}
						actions={
							editing || connection.scopes.length < 2 ? null : (
								<Button variant="quiet" onClick={() => setEditing(true)}>
									{text('connections.permissions.change')}
								</Button>
							)
						}
					>
						<Permissions
							label={text('connections.scopes')}
							scopes={connection.scopes}
							chosen={kept}
							onChange={next => setRemoved(names.filter(name => !next.includes(name)))}
							product={connection.product.name}
							disabled={!editing || busy}
							marks={Object.fromEntries(removed.map(name => [name, 'remove' as const]))}
						/>
					</Section>
					<Section level={3} title={text('connections.connection')}>
						<Facts
							label={connection.client.name}
							rows={[
								{ key: 'product', label: text('connect.product'), value: connection.product.name },
								{ key: 'organization', label: text('connect.organization'), value: reached(connection) },
								host ? { key: 'host', label: text('connect.host'), value: <span className="break-all">{host}</span>, mono: true } : null,
								{ key: 'connected', label: text('connections.connected'), value: date(connection.created) },
								{ key: 'used', label: text('connections.last'), value: connection.used ? date(connection.used) : text('connections.never') },
								{ key: 'ends', label: text('connections.ends'), value: date(connection.expires) }
							]}
						/>
					</Section>
				</>
			) : null}
		</SideSheet>
	);
}
